// Tiny inline-SVG chart helpers. No external deps.
// All functions return HTML strings that are safe to drop into `set:html`.

export interface SparkPoint {
  label: string; // e.g. "Mar 26"
  value: number;
}

interface Coord { x: number; y: number; value: number; label: string }

/**
 * Render a sparkline as an inline SVG.
 * Sized responsive via viewBox; colors come from CSS custom properties.
 */
export function renderSparkline(points: SparkPoint[], opts: { height?: number; width?: number; fill?: string; stroke?: string; peak?: boolean } = {}): string {
  const w = opts.width ?? 280;
  const h = opts.height ?? 44;
  const padX = 2;
  const padY = 4;
  const stroke = opts.stroke ?? "var(--primary600)";
  const fill = opts.fill ?? "color-mix(in srgb, var(--primary400) 22%, transparent)";

  if (points.length === 0) {
    return `<svg viewBox="0 0 ${w} ${h}" class="sparkline sparkline--empty" aria-hidden="true"><line x1="${padX}" x2="${w - padX}" y1="${h / 2}" y2="${h / 2}" stroke="var(--hairline)" stroke-dasharray="2 3"/></svg>`;
  }

  const values = points.map((p) => p.value);
  const max = Math.max(...values, 1);
  const min = 0;
  const range = Math.max(max - min, 1);
  const stepX = (w - padX * 2) / Math.max(points.length - 1, 1);

  const coords: Coord[] = points.map((p, i) => {
    const x = padX + i * stepX;
    const y = padY + (1 - (p.value - min) / range) * (h - padY * 2);
    return { x, y, value: p.value, label: p.label };
  });

  const first = coords[0]!;
  const last = coords[coords.length - 1]!;

  const line = coords.map((c, i) => `${i === 0 ? "M" : "L"}${c.x.toFixed(1)},${c.y.toFixed(1)}`).join(" ");
  const area = `${line} L${last.x.toFixed(1)},${h - padY} L${first.x.toFixed(1)},${h - padY} Z`;

  let peakIdx = 0;
  for (let i = 1; i < coords.length; i++) {
    if (coords[i]!.value > coords[peakIdx]!.value) peakIdx = i;
  }
  const peak: Coord | null = opts.peak !== false ? coords[peakIdx]! : null;

  const firstLabel = points[0]!.label;
  const lastLabel = points[points.length - 1]!.label;
  const aria = `${firstLabel}–${lastLabel}: min ${min}, max ${max}, latest ${last.value}`;

  return `
    <svg viewBox="0 0 ${w} ${h}" class="sparkline" role="img" aria-label="${aria}" preserveAspectRatio="none">
      <path d="${area}" fill="${fill}" />
      <path d="${line}" fill="none" stroke="${stroke}" stroke-width="1.5" stroke-linecap="round" stroke-linejoin="round"/>
      ${peak ? `<circle cx="${peak.x.toFixed(1)}" cy="${peak.y.toFixed(1)}" r="2.4" fill="${stroke}" />` : ""}
      <circle cx="${last.x.toFixed(1)}" cy="${last.y.toFixed(1)}" r="2.8" fill="var(--surface)" stroke="${stroke}" stroke-width="1.5" />
    </svg>
  `;
}

/**
 * Build month-bucket series for the last N months.
 * `rows` is anything with an ISO date field + a numeric field; bucket by earned-at month.
 * Returns oldest-first so sparkline draws left→right.
 */
export function monthlyBuckets<T extends Record<string, unknown>>(
  rows: T[],
  dateKey: keyof T,
  valueKey: keyof T,
  months: number,
  now: Date = new Date(),
): SparkPoint[] {
  const buckets: Record<string, number> = {};
  const keys: string[] = [];
  const fmtLabel = (d: Date) => d.toLocaleDateString("en-GB", { month: "short", year: "2-digit" });

  for (let i = months - 1; i >= 0; i--) {
    const d = new Date(Date.UTC(now.getUTCFullYear(), now.getUTCMonth() - i, 1));
    const key = `${d.getUTCFullYear()}-${String(d.getUTCMonth() + 1).padStart(2, "0")}`;
    buckets[key] = 0;
    keys.push(key);
  }

  for (const row of rows) {
    const dateVal = row[dateKey] as unknown as string | null;
    if (!dateVal) continue;
    const d = new Date(dateVal);
    if (Number.isNaN(d.getTime())) continue;
    const key = `${d.getUTCFullYear()}-${String(d.getUTCMonth() + 1).padStart(2, "0")}`;
    if (!(key in buckets)) continue;
    const v = Number(row[valueKey] ?? 0);
    buckets[key] = (buckets[key] ?? 0) + (Number.isFinite(v) ? v : 0);
  }

  return keys.map((k) => {
    const parts = k.split("-").map(Number);
    const y = parts[0] ?? 0;
    const m = parts[1] ?? 1;
    return {
      label: fmtLabel(new Date(Date.UTC(y, m - 1, 1))),
      value: buckets[k] ?? 0,
    };
  });
}

/**
 * Percentage delta between `current` and `previous`, with a human-readable formatter.
 * Returns null if previous is zero (avoid divide-by-zero / misleading infinity).
 */
export function pctDelta(current: number, previous: number): { pct: number; direction: "up" | "down" | "flat"; label: string } | null {
  if (!Number.isFinite(previous) || previous === 0) return null;
  const pct = ((current - previous) / Math.abs(previous)) * 100;
  const direction = pct > 0.5 ? "up" : pct < -0.5 ? "down" : "flat";
  const sign = pct > 0 ? "+" : "";
  return { pct, direction, label: `${sign}${pct.toFixed(1)}%` };
}
